import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Flag, IdCard } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { fetchFeatureFlags, type AdminFeatureFlag } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A14.01 — Feature-flag ro'yxati. GET /admin/settings/flags (backend `3cc1a1d`).
 * Faqat `feature_*` (boshqa config kalitlar secret bo'lishi mumkin). O'zgartirish
 * (A14.03 yoqish/o'chirish) = mutatsiya-endpoint + tasdiq bilan follow-up.
 */
export function FeatureFlags() {
  const t = useT()
  const navigate = useNavigate()

  const rowActions = useMemo<RowAction<AdminFeatureFlag>[]>(
    () => [
      {
        id: 'card',
        label: t.users.viewCard,
        icon: <IdCard size={13} />,
        onSelect: (r) => navigate(`/flags/${encodeURIComponent(r.key)}`),
      },
    ],
    [t, navigate],
  )

  const columns = useMemo<AdminColumn<AdminFeatureFlag>[]>(
    () => [
      { key: 'key', header: t.flags.key, type: 'text', minWidth: 240, render: (r) => (
        <span className="font-mono text-sm text-primary">{r.key}</span>
      ) },
      {
        key: 'enabled',
        header: t.col.status,
        type: 'enum',
        width: 130,
        accessor: (r) => (r.enabled ? 'on' : 'off'),
        options: [
          { value: 'on', label: t.flags.on, status: 'active' },
          { value: 'off', label: t.flags.off, status: 'deleted' },
        ],
      },
      { key: 'value', header: t.flags.value, type: 'text', width: 120, render: (r) => (
        <span className="font-mono text-sm text-secondary">{r.value}</span>
      ) },
      { key: 'updated_at', header: t.finance.updated, type: 'date', width: 160 },
    ],
    [t],
  )

  const fetcher = useCallback(fetchFeatureFlags, [])

  return (
    <>
      <PageHeader title={t.flags.title} screenId="A14.01" />
      <DataTable<AdminFeatureFlag>
        screenId="A14.01"
        permission={PERMISSIONS.AUDIT_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => r.key}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <Flag size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
